import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  BarChart3, ArrowLeft, Loader2, Award, XCircle, CheckCircle2,
  TrendingDown, TrendingUp, Truck, DollarSign
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteComparisonView = () => {
  const navigate = useNavigate();
  const { rfq_number } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState(null);
  const [awarding, setAwarding] = useState(false);


  const loadComparison = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/vendor-quotes/rfq/${rfq_number}/comparison`);
      setComparison(response.data);
    } catch (error) {
      console.error('Error loading comparison:', error);
      toast.error('Failed to load quote comparison');
      navigate('/vendor-quotes');
    } finally {
      setLoading(false);
    }
  }, [rfq_number, navigate]);

  useEffect(() => {
    loadComparison();
  }, [loadComparison]);

  const handleAwardQuote = async (quotationNumber) => {
    if (!window.confirm(`Award quotation ${quotationNumber} and create PO?`)) return;
    
    try {
      setAwarding(true);
      const response = await axios.post(`${BACKEND_URL}/api/vendor-quotes/quotations/award`, {
        quotation_number: quotationNumber,
        awarded_by: "ADMIN",
        create_po: true
      });
      toast.success(response.data.message);
      loadComparison();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to award quotation');
    } finally {
      setAwarding(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading quote comparison...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!comparison) return null;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="quote-comparison-view">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/vendor-quotes')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-7 h-7 text-orange-600" />
                Quote Comparison: {rfq_number}
              </h1>
              <p className="text-sm text-gray-500">Compare vendor quotes and award to winner</p>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-600">Vendors Invited</p>
            <p className="text-2xl font-bold text-gray-900">{comparison.total_vendors_invited}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-600">Quotes Received</p>
            <p className="text-2xl font-bold text-orange-600">{comparison.total_quotes_received}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-600">Response Rate</p>
            <p className="text-2xl font-bold text-green-600">{comparison.response_rate}%</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-600">Recommended Vendor</p>
            <p className="text-xl font-bold text-emerald-600">{comparison.recommended_vendor || '-'}</p>
            <p className="text-xs text-gray-500">{comparison.recommendation_reason}</p>
          </div>
        </div>

        {/* Item Comparisons */}
        {comparison.items.map((item) => (
          <div key={item.item_number} className="bg-white rounded-lg shadow-sm border mb-6">
            <div className="p-4 border-b bg-gray-50">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Item {item.item_number}: {item.short_text}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Material: {item.material_id || 'N/A'} | Qty: {item.requested_quantity} {item.unit_of_measure} | Plant: {item.plant}
                  </p>
                </div>
                <div className="text-right">
                  {item.lowest_price_vendor && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <TrendingDown className="w-4 h-4" />
                      Lowest: {item.lowest_price_vendor} @ {formatCurrency(item.lowest_price)}
                    </p>
                  )}
                  {item.earliest_delivery_vendor && (
                    <p className="text-sm text-orange-600 flex items-center gap-1">
                      <Truck className="w-4 h-4" />
                      Earliest: {item.earliest_delivery_vendor} ({item.earliest_delivery_date})
                    </p>
                  )}
                </div>
              </div>
            </div>

            {item.vendor_quotes.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No quotes received for this item
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quotation</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quoted Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivery Date</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {item.vendor_quotes.map((vq) => (
                      <tr
                        key={vq.vendor_number || vq.quotation_number}
                        className={`hover:bg-gray-50 ${vq.rank === 1 ? 'bg-green-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold ${
                            vq.rank === 1 ? 'bg-green-500 text-white' : 
                            vq.rank === 2 ? 'bg-yellow-500 text-white' : 
                            'bg-gray-200 text-gray-600'
                          }`}>
                            {vq.rank}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{vq.vendor_id}</p>
                          <p className="text-xs text-gray-500">{vq.vendor_name}</p>
                        </td>
                        <td className="px-4 py-3 text-orange-600 font-medium">{vq.quotation_number}</td>
                        <td className="px-4 py-3 text-right font-medium">
                          {formatCurrency(vq.unit_price, vq.currency)}
                        </td>
                        <td className="px-4 py-3 text-right">{vq.quoted_quantity}</td>
                        <td className="px-4 py-3 text-right font-bold text-emerald-700">
                          {formatCurrency(vq.total, vq.currency)}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{vq.delivery_date}</td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            size="sm"
                            onClick={() => handleAwardQuote(vq.quotation_number)}
                            disabled={awarding}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Award className="w-4 h-4 mr-1" />Award
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
};

export default QuoteComparisonView;
